import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";

import { FileHelper } from "@/common/helpers/file";
import { HttpHelper } from "@/common/helpers/http";
import { MimeTypeHelper } from "@/common/helpers/mimeType";
import { TypeHelper } from "@/common/helpers/type";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { ApiEnumName } from "@/common/services/enum";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  DataExportConfigDto,
  DataExportRequestAllFilteredScopeDto,
  EntityType,
  ExportScope,
  ExportType,
} from "@/core/api/generated/v1";

import AppButton from "../Button/AppButton";
import AppIconButton from "../Button/AppIconButton";
import FoldableBlock from "../Display/FoldableBlock";
import DataExportConfigAutocomplete from "../Entity/DataExportConfig/DataExportConfigAutocomplete";
import InlineApiEnumValue from "../Enum/InlineApiEnumValue";
import AppIcon from "../Icons/AppIcon";
import ConfirmationModal from "../Modals/ConfirmationModal";
import AppTypography from "../Text/AppTypography";
import AppTooltip from "../Tooltip/AppTooltip";

interface ExportDisplayProps {
  configs?: boolean;
  scope?: boolean;
  mime?: boolean;
  properties?: boolean;
}

const defaultExportDisplayProps: ExportDisplayProps = {
  configs: true,
  mime: true,
  properties: true,
  scope: true,
};

interface ExportDefaultValues {
  scope: ExportScope;
  mime?: string;
}

const defaultExportValues: ExportDefaultValues = {
  scope: ExportScope.CurrentPage,
  mime: MimeTypeHelper.getSpreadsheetFormats().filter((x) => x.extension === "csv")[0].mimeType,
};

export interface DataExportProps {
  type: ExportType;
  primaryEntityType?: EntityType;
  primaryEntityId?: string;
  currentPageScope?: {
    ids: string[] | undefined;
  };
  allFilteredScope?: DataExportRequestAllFilteredScopeDto;
  displayProps?: ExportDisplayProps;
  defaultValues?: ExportDefaultValues;
  onExportDone?: () => void;
}

export interface DataExportFormikValues {
  params: {
    scope: ExportScope;
    mime: string; // MIME type for the export
    properties: string[]; // Selected properties for the export
  };
  config?: DataExportConfigDto; // Fetched export configuration
  isUpdateConfig?: boolean; // Whether to update the selected export configuration
  saveConfigIdentifier?: string; // Create configuration
}

export default function DataExportForm({
  type,
  primaryEntityType,
  primaryEntityId,
  currentPageScope,
  allFilteredScope,
  displayProps,
  defaultValues,
  onExportDone,
}: DataExportProps) {
  displayProps = { ...defaultExportDisplayProps, ...displayProps };
  defaultValues = { ...defaultExportValues, ...defaultValues };

  const { enqueueSnackbar } = useAppSnackbar();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  // const [isRenameModalOpen, setIsRenameModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const isCurrentPageScopeEnabled = !TypeHelper.isEmptyArray(currentPageScope?.ids);
  const isAllFilteredScopeEnabled = !!allFilteredScope;

  const formik = useFormik<DataExportFormikValues>({
    enableReinitialize: true,
    initialValues: {
      params: {
        scope: defaultValues.scope,
        mime: defaultValues.mime!,
        properties: [],
      },
      config: undefined, // Fetched export configuration
      isUpdateConfig: false, // Whether to update the selected export configuration
      saveConfigIdentifier: undefined,
    },
    onSubmit: async (values, { setFieldError, setStatus, setSubmitting }) => {
      try {
        setIsLoading(true);
        if (values.isUpdateConfig) {
          // Update the selected export configuration
          await apiClient.dataExportApi.apiV1DataExportConfigsIdPut({
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            id: values.config!.id!,
            dataExportConfigUpdateDto: {
              type: type,
              primaryEntity:
                primaryEntityType && primaryEntityId
                  ? {
                      entityType: primaryEntityType,
                      entityId: primaryEntityId,
                    }
                  : undefined,
              mimeType: values.params.mime,
              properties: values.params.properties,
            },
          });
        }

        const response = await apiClient.dataExportApi.apiV1DataExportPost(
          {
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            dataExportRequestDto: {
              type: type,
              scope: values.params.scope,
              primaryEntity:
                primaryEntityType && primaryEntityId
                  ? {
                      entityType: primaryEntityType,
                      entityId: primaryEntityId,
                    }
                  : undefined,
              mimeType: values.params.mime,
              properties: values.params.properties,
              currentPageScope:
                values.params.scope === ExportScope.CurrentPage &&
                isCurrentPageScopeEnabled &&
                currentPageScope
                  ? {
                      ...currentPageScope,
                    }
                  : undefined,
              allFilteredScope:
                values.params.scope === ExportScope.AllFiltered &&
                isAllFilteredScopeEnabled &&
                allFilteredScope
                  ? {
                      ...allFilteredScope,
                    }
                  : undefined,
              allScope: values.params.scope === ExportScope.All ? {} : undefined,
            },
          },
          {
            responseType: "blob",
          },
        );
        const parsedContent = HttpHelper.parseContentHeaders(response.headers);
        const downloadedFile = {
          blob: response.data as Blob,
          contentType: parsedContent.contentType!,
          filename: parsedContent.contentDispositionInfo?.directives?.filename,
        };
        enqueueSnackbar("Export file is ready.", { variant: "success" });
        await FileHelper.downloadBlobFromApiResult(downloadedFile);
        onExportDone && onExportDone();

        setStatus({ success: true });
        setSubmitting(false);
      } catch (err) {
        console.error("Error during export: ", err);

        enqueueSnackbar(`Export failed.`, { variant: "error" });

        ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
        setStatus({ success: false });
        setSubmitting(false);
      } finally {
        setIsLoading(false);
      }
    },
  });

  const { values, setFieldValue } = formik;

  const propertiesRequest = useApiRequest(
    apiClient.dataExportApi.apiV1DataExportPropertiesGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      type: type,
    },
    {
      deps: [type],
      skip: !type,
    },
  );

  useEffect(() => {
    if (propertiesRequest.data) {
      formik.setFieldValue(
        "params.properties",
        propertiesRequest.data.map((field) => field!) || [],
      );
    }
  }, [propertiesRequest.data]);

  // whether the current configuration is changed compared to the fetched config
  const isFetchedConfigModified = useMemo(() => {
    return (
      values.config &&
      (values.params.mime !== values.config?.mimeType ||
        !_.isEqual(values.params.properties?.sort(), values.config?.properties?.sort()))
    );
  }, [values.config, values.params]);

  return (
    <>
      <Stack direction='column' spacing={2} sx={{}}>
        {/* Config */}
        {displayProps.configs && propertiesRequest.data && (
          <Stack direction='row' spacing={2} sx={{ width: "100%" }}>
            <FormControl fullWidth>
              <DataExportConfigAutocomplete
                fullWidth
                label='Select configuration'
                searchFilters={{
                  type: type,
                  primaryEntity:
                    primaryEntityType && primaryEntityId
                      ? {
                          entityType: primaryEntityType,
                          entityId: primaryEntityId,
                        }
                      : undefined,
                }}
                entity={formik.values.config}
                onChange={(newValue) => {
                  if (newValue) {
                    formik.setValues({
                      ...formik.values,
                      params: {
                        ...formik.values.params,
                        scope: formik.values.params.scope,
                        mime: newValue.mimeType!,
                        properties:
                          newValue?.properties?.filter((x) =>
                            propertiesRequest.data?.includes(x),
                          ) || [],
                      },
                      config: newValue,
                    });
                  } else {
                    setFieldValue("config", undefined);
                  }
                }}
              />
            </FormControl>
            {!!values.config && (
              <AppTooltip title='Delete configuration'>
                <AppIconButton onClick={() => setIsDeleteModalOpen(true)}>
                  <AppIcon of='delete' />
                </AppIconButton>
              </AppTooltip>
            )}
          </Stack>
        )}

        {/* Mime Type */}
        {displayProps?.mime && (
          <FormControl sx={{ minWidth: { xxs: "100%" } }}>
            <InputLabel required>File format</InputLabel>
            <Select
              label='File format'
              value={values.params.mime}
              onChange={(e) => setFieldValue("params.mime", e.target.value)}
            >
              {MimeTypeHelper.getSpreadsheetFormats().map((format, index) => (
                <MenuItem key={index} value={format.mimeType}>
                  {format.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Properties */}
        {propertiesRequest.isLoading && <LinearProgress />}
        {!propertiesRequest.isLoading && displayProps?.properties && (
          <FormControl sx={{ width: "100%" }}>
            <InputLabel required>Properties</InputLabel>
            <Select
              multiple
              label='Properties'
              value={values.params.properties}
              renderValue={() => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {values.params.properties.map((value) => (
                    <Chip
                      key={value}
                      label={value}
                      onMouseDown={(e) => e.stopPropagation()}
                      onDelete={(e) => {
                        e.stopPropagation();
                        setFieldValue(
                          "params.properties",
                          values.params.properties.filter((x) => x !== value),
                        );
                      }}
                    />
                  ))}
                </Box>
              )}
              onChange={(e) => {
                if (e.target.value) {
                  setFieldValue(
                    "params.properties",
                    typeof e.target.value === "string"
                      ? (e.target.value as string).split(",")
                      : (e.target.value as string[]),
                  );
                }
              }}
            >
              {propertiesRequest.data?.map((field, index) => (
                <MenuItem key={index} value={field!}>
                  {field}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Export Mode */}
        {displayProps?.scope && (
          <FormControl>
            <FormLabel>Export scope</FormLabel>
            <RadioGroup
              row
              value={values.params.scope}
              onChange={(e, v) => setFieldValue("params.scope", v as ExportScope)}
            >
              {isCurrentPageScopeEnabled && (
                <FormControlLabel
                  value={ExportScope.CurrentPage}
                  control={<Radio />}
                  label={
                    <InlineApiEnumValue
                      type={ApiEnumName.ExportScope}
                      value={ExportScope.CurrentPage}
                      withHelperTooltip
                    />
                  }
                />
              )}

              {isAllFilteredScopeEnabled && (
                <FormControlLabel
                  value={ExportScope.AllFiltered}
                  control={<Radio />}
                  label={
                    <InlineApiEnumValue
                      type={ApiEnumName.ExportScope}
                      value={ExportScope.AllFiltered}
                      withHelperTooltip
                    />
                  }
                />
              )}

              <FormControlLabel
                value={ExportScope.All}
                control={<Radio />}
                label={
                  <InlineApiEnumValue
                    type={ApiEnumName.ExportScope}
                    value={ExportScope.All}
                    withHelperTooltip
                  />
                }
              />
            </RadioGroup>
          </FormControl>
        )}

        <Stack spacing={1}>
          {isFetchedConfigModified && (
            <FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    name='isUpdateConfig'
                    checked={values.isUpdateConfig}
                    onChange={(e) => setFieldValue("isUpdateConfig", e.target.checked)}
                  />
                }
                label='Update selected configuration'
              />
            </FormControl>
          )}

          {/* Save Config */}
          {displayProps?.configs && !values.config && (
            <FoldableBlock
              defaultIsFolded
              trigger={{
                label: (
                  <AppTypography>
                    Save configuration{" "}
                    <AppTooltip title='You can save the current export settings for future use'>
                      <AppIcon of='help' inText />
                    </AppTooltip>
                  </AppTypography>
                ),
              }}
            >
              <Stack direction='row' spacing={1} sx={{ alignItems: "center", width: "50%" }}>
                <FormControl margin='dense' sx={{ flex: 1 }}>
                  <TextField
                    size='small'
                    label='Configuration name'
                    value={values.saveConfigIdentifier}
                    onChange={(e) => setFieldValue("saveConfigIdentifier", e.target.value)}
                  />
                </FormControl>
                <AppButton
                  variant='contained'
                  color='primary'
                  sx={{ flex: 1, maxWidth: "100px" }}
                  onClick={async () => {
                    try {
                      await apiClient.dataExportApi.apiV1DataExportConfigsPost({
                        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                        dataExportConfigCreateDto: {
                          identifier: values.saveConfigIdentifier,
                          type: type,
                          primaryEntity: {
                            entityType: primaryEntityType,
                            entityId: primaryEntityId,
                          },
                          mimeType: values.params.mime,
                          properties: values.params.properties,
                        },
                      });
                      setFieldValue("saveConfigIdentifier", "");
                      enqueueSnackbar("Configuration saved successfully", { variant: "success" });
                    } catch (err) {
                      enqueueSnackbar(ValidationHelper.handleApiErrorResponse(err).generalError, {
                        variant: "error",
                      });
                    }
                  }}
                >
                  Save
                </AppButton>
              </Stack>
            </FoldableBlock>
          )}
        </Stack>

        <AppButton
          loading={isLoading}
          variant='contained'
          color='primary'
          onClick={() => formik.handleSubmit()}
          disabled={!type || !values.params.mime || values.params.properties.length === 0}
        >
          Export
        </AppButton>
      </Stack>

      {/* Delete Modal */}
      <ConfirmationModal
        title='Delete configuration'
        body='Are you sure you want to delete this configuration?'
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={async () => {
          try {
            await apiClient.dataExportApi.apiV1DataExportConfigsIdDelete({
              nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
              id: values.config!.id!,
            });
            setFieldValue("config", undefined);
            setIsDeleteModalOpen(false);
          } catch (err) {
            console.error("Error deleting configuration: ", err);
            enqueueSnackbar("Error deleting configuration", { variant: "error" });
          }
        }}
      />
    </>
  );
}
